import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollReveal from '../../components/ui/ScrollReveal';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useSEO } from '../../hooks/useSEO';

const services = [
  {
    num: '01',
    title: '1-2-1 Personal Training',
    tag: 'In-Person · London',
    to: '/personal-training',
    img: '/images/MIH02354_3.jpg',
    imgPos: 'object-[50%_15%]',
    tagline: 'The gold standard in personal training — dedicated, bespoke, and results-guaranteed.',
    desc: "Train one-on-one with Laura at a premium London gym. Every session is carefully programmed and adapted in real-time to your body and energy levels on the day.",
    includes: ['Fully bespoke programme — written fresh for you','Technique coaching & form correction every session','Progressive overload tracking week by week','Nutrition guidance included with all packages','WhatsApp support between sessions','Monthly progress reviews with body measurements'],
  },
  {
    num: '02',
    title: 'Bespoke Online Coaching',
    tag: 'Remote · Worldwide',
    to: '/online-training',
    img: '/images/IMG_9778_edited_edited_edited.jpg',
    imgPos: 'object-[50%_15%]',
    tagline: 'World-class coaching delivered straight to your phone — wherever you are.',
    desc: "Laura's online coaching gives you the same quality as in-person training, delivered through a dedicated coaching app. Train at your own gym, at home, or anywhere in the world.",
    includes: ['Custom 12-week+ training programme','Dedicated coaching app with video demonstrations','Weekly check-in calls with Laura','Nutrition & macro targets tailored to your goals','24/7 WhatsApp support','Biweekly programme adjustments based on progress'],
  },
  {
    num: '03',
    title: 'Nutritional Coaching',
    tag: 'Remote · Worldwide',
    to: '/nutritional-coaching',
    img: '/images/IMG_9847_edited.jpg',
    imgPos: 'object-[50%_15%]',
    tagline: 'Fuel your transformation from the inside out — no fads, just results.',
    desc: "Sustainable nutrition is the foundation of every successful transformation. Laura cuts through the noise with a clear, personalised strategy that fits your lifestyle.",
    includes: ['Full nutritional assessment & lifestyle audit','Custom calorie & macro targets','Flexible meal framework — no rigid meal plans','Supplement guidance where appropriate','Ongoing adjustments as your body adapts','Education: understand your nutrition for life'],
  },
];

const faqs = [
  { q: 'Where are 1-2-1 sessions based in London?', a: "Laura trains clients at premium private gym facilities across London. The exact location is confirmed at consultation based on what's most convenient for you." },
  { q: 'How do I know which service is right for me?', a: "Book a free consultation and Laura will assess your goals, lifestyle, and experience level to recommend the most effective route. No obligation." },
  { q: 'Can I combine services?', a: "Absolutely. Many clients combine 1-2-1 training with nutrition coaching, or start online before moving to in-person. Laura will advise on the best combination." },
  { q: 'How quickly will I see results?', a: "Most clients notice meaningful changes within 4–6 weeks. Significant body composition changes typically appear within 8–12 weeks, depending on consistency." },
  { q: 'Do you work with complete beginners?', a: "Yes. Laura loves working with beginners and creates a safe, non-intimidating environment. Your programme is always built to your current level." },
];

export default function Services() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useSEO({
    title: 'Personal Training Services London | Laura Martin',
    description:
      '1-2-1 personal training, bespoke online coaching & nutritional support in London. Tailored programmes for real, lasting results.',
  });

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative h-[50vh] min-h-[400px] lg:h-[70vh] lg:min-h-[560px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1800&q=80"
              alt="Premium personal training services in London gym"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1c1917]/80 via-[#1c1917]/45 to-transparent" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #faf8f5 0%, rgba(250,248,245,0.35) 18%, transparent 42%)' }} />
          </div>
          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-36">
            <ScrollReveal className="absolute bottom-0 pb-10 lg:pb-24 max-w-3xl">
              <div className="flex items-center gap-4 mb-6">
                <span className="w-8 h-px bg-[#c9a96e]" />
                <span className="text-[#c9a96e] text-[10px] font-sans font-semibold tracking-[0.32em] uppercase">What I Offer</span>
              </div>
              <h1 className="text-white mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.8rem, 6vw, 5rem)', fontWeight: 600, lineHeight: 1.0 }}>
                Services Built
                <br />
                <em className="text-[#c9a96e] not-italic">Around You.</em>
              </h1>
              <p className="text-white/75 font-sans text-base max-w-lg leading-relaxed">
                Whether you want in-person PT in London, online coaching, or expert nutritional guidance — Laura has a programme for you.
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Service rows */}
        <section className="bg-[#faf8f5] py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col gap-6">
            {services.map((s, i) => (
              <ScrollReveal key={s.num} delay={i * 80}>
                <div className="bg-white rounded-2xl overflow-hidden border border-stone-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <div className={`grid grid-cols-1 lg:grid-cols-2 ${i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                    {/* Image */}
                    <div className="relative h-72 lg:h-auto overflow-hidden min-h-[300px]">
                      <img
                        src={s.img}
                        alt={`${s.title} programme with Laura Martin personal trainer London`}
                        loading="lazy"
                        className={`w-full h-full object-cover ${s.imgPos}`}
                      />
                      <span className="absolute top-5 left-5 text-stone-900/8 font-display font-bold text-6xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.num}</span>
                      <span className="absolute top-5 right-5 px-3 py-1 bg-white/85 backdrop-blur-sm text-stone-600 text-[9px] font-sans tracking-widest uppercase rounded-full border border-stone-200">{s.tag}</span>
                    </div>
                    {/* Content */}
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <h3 className="text-stone-900 text-3xl mb-2 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.title}</h3>
                      <p className="text-[#c9a96e] font-sans text-xs tracking-wide mb-5 italic">{s.tagline}</p>
                      <p className="text-stone-600 font-sans text-sm leading-relaxed mb-6">{s.desc}</p>
                      <ul className="space-y-2 mb-7">
                        {s.includes.map((item) => (
                          <li key={item} className="flex items-start gap-3 text-stone-600 font-sans text-sm">
                            <span className="w-4 h-4 rounded-full bg-[#c9a96e]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-check-line text-[#c9a96e] text-[10px]" />
                            </span>
                            {item}
                          </li>
                        ))}
                      </ul>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <p className="text-stone-500 font-sans text-sm">Get in touch for pricing and package details.</p>
                        <Link to="/contact" className="px-7 py-3 bg-[#c9a96e] text-white text-[10px] font-sans font-bold tracking-[0.25em] uppercase rounded-full hover:bg-[#a8863f] hover:shadow-lg hover:shadow-[#c9a96e]/20 hover:-translate-y-0.5 transition-all duration-300 w-fit">
                          Enquire Now
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-stone-100 py-12 lg:py-16">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <ScrollReveal className="mb-8 text-center">
              <div className="flex items-center justify-center gap-4 mb-5">
                <span className="w-8 h-px bg-[#c9a96e]" />
                <span className="text-[#c9a96e] text-[10px] font-sans font-semibold tracking-[0.32em] uppercase">FAQ</span>
                <span className="w-8 h-px bg-[#c9a96e]" />
              </div>
              <h2 className="text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2.5rem', fontWeight: 600 }}>
                Frequently Asked Questions
              </h2>
            </ScrollReveal>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <ScrollReveal key={i} delay={i * 60}>
                  <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    <button className="w-full text-left px-7 py-5 flex items-center justify-between gap-4" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                      <h3 className="text-stone-900 font-sans text-sm font-medium leading-snug">{f.q}</h3>
                      <i className={`ri-add-line text-[#c9a96e] text-lg flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-45' : ''}`} />
                    </button>
                    {openFaq === i && (
                      <div className="px-7 pb-6 border-t border-stone-100">
                        <p className="text-stone-600 font-sans text-sm leading-relaxed pt-4">{f.a}</p>
                      </div>
                    )}
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
